/**
 * Template State Atoms
 * Phase 2 Sprint 14: Template State Management
 * 
 * Jotai atoms for managing template selection and loading state
 */

import { atom } from 'jotai';
import { Template } from '../../../types/template';

/**
 * Selected template atom
 * Holds the currently selected template for preview or use
 */
export const selectedTemplateAtom = atom<Template | null>(null);

/**
 * Template preview visibility atom
 * Controls whether the preview modal is open
 */
export const templatePreviewVisibleAtom = atom<boolean>(false);

/**
 * Loaded template atom
 * Holds the template that has been loaded into the recorder
 */
export const loadedTemplateAtom = atom<Template | null>(null);

/**
 * Template loading state atom
 * Tracks whether a template is currently being loaded
 */
export const templateLoadingAtom = atom<boolean>(false);

/**
 * Template load error atom
 * Stores any error that occurred during template loading
 */
export const templateLoadErrorAtom = atom<string | null>(null);

/**
 * Derived atom: Has loaded template
 * Returns true if a template is currently loaded in the recorder
 */
export const hasLoadedTemplateAtom = atom(
  (get) => get(loadedTemplateAtom) !== null
);

/**
 * Derived atom: Template load status
 * Provides a combined status of the template loading process
 */
export const templateLoadStatusAtom = atom((get) => {
  const loading = get(templateLoadingAtom);
  const error = get(templateLoadErrorAtom);
  const loaded = get(loadedTemplateAtom);

  if (loading) return 'loading';
  if (error) return 'error';
  if (loaded) return 'loaded';
  return 'idle';
});
